import * as React from "react";
import { formatCredits, formatCreditsUsd } from "@/lib/format";
import { cn } from "@/lib/utils";

export interface SpendMeterProps extends React.HTMLAttributes<HTMLDivElement> {
  used: number; // in credits
  reserved?: number; // in credits
  limit: number; // in credits
  label?: string;
  showLabels?: boolean;
}

/**
 * SpendMeter visualises budget utilisation with used and reserved segments,
 * presenting dollars beside credits for clarity.
 */
export function SpendMeter({
  used,
  reserved = 0,
  limit,
  label = "Spend and budget cap",
  showLabels = true,
  className,
  ...props
}: SpendMeterProps) {
  const safeLimit = Math.max(1, limit);
  const usedRatio = Math.min(Math.max(0, used) / safeLimit, 1);
  const reservedRatio = Math.min(Math.max(0, reserved) / safeLimit, 1 - usedRatio);

  const usedPct = (usedRatio * 100).toFixed(1);
  const reservedPct = (reservedRatio * 100).toFixed(1);

  const totalCommitted = used + reserved;
  const isOverLimit = totalCommitted > safeLimit;

  return (
    <div className={cn("flex flex-col gap-2", className)} {...props}>
      <div
        role="meter"
        aria-label={label}
        aria-valuenow={used}
        aria-valuemin={0}
        aria-valuemax={limit}
        aria-valuetext={`${formatCredits(used)} credits (${formatCreditsUsd(used)}) used of ${formatCredits(limit)} credits limit`}
        className="relative h-2 w-full overflow-hidden rounded-full bg-muted"
      >
        <div
          className="absolute inset-y-0 left-0 rounded-l-full bg-primary transition-all"
          style={{ width: `${usedPct}%` }}
        />
        {reserved > 0 ? (
          <div
            className="absolute inset-y-0 rounded-r-full bg-info/70 transition-all"
            style={{
              left: `${usedPct}%`,
              width: `${reservedPct}%`,
            }}
          />
        ) : null}
      </div>

      {showLabels ? (
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs tabular-nums text-muted-foreground">
          <div className="flex flex-wrap items-center gap-3">
            <span>
              <strong className="font-semibold text-foreground">{formatCredits(used)}</strong>{" "}
              credits ({formatCreditsUsd(used)}) used
            </span>
            {reserved > 0 ? (
              <span className="text-info">
                +<strong>{formatCredits(reserved)}</strong> credits ({formatCreditsUsd(reserved)})
                reserved
              </span>
            ) : null}
          </div>
          <span className={cn(isOverLimit ? "font-semibold text-warning" : "")}>
            Limit: {formatCredits(limit)} credits ({formatCreditsUsd(limit)})
          </span>
        </div>
      ) : null}
    </div>
  );
}
